import { useQuery } from '@apollo/client'
import { ModelContext } from 'domain/_model/model.context'
import { User, UserRole } from 'domain/_entities/user.entity'
import { GET_USER } from 'data/users/user/user.data.source'
import { useMemo } from 'react'
import {
  CustomerRoleType,
  hasUserEnabledFeaturesFn,
  hasUserPermissionsFn,
  isUserInRoles as isUserInRolesFn,
} from 'domain/users/user/customer_role_check'
import {
  ADMIN_PERMISSIONS,
  Permission,
  PermissionType,
  USER_PERMISSIONS,
} from 'domain/_entities/permission.entity'
import { RolePermissionRow } from 'domain/_entities/role.entity'
import { FeatureSwitch } from 'models/customer'

/**
 * Returning User Model Context for useUserModel
 */
export interface UserModelContext extends Omit<ModelContext<User>, 'data'> {
  /** User data */
  user: User
  /** Whether the user is in specific roles */
  isUserInRoles: (roles: CustomerRoleType[]) => boolean
  /** Whether the user has specific permissions */
  hasUserPermissions: (permission: PermissionType | PermissionType[]) => boolean
  /** Whether the user has enabled features */
  hasUserEnabledFeatures: (feature: FeatureSwitch[]) => boolean
}

/**
 * Get user data model
 * @param props
 * @category User
 */
export function useUserModel(props: {
  /** User id */
  userId: string | undefined
}): UserModelContext {
  const { userId } = props
  const { loading, error, data, refetch } = useQuery(GET_USER, {
    variables: { id: userId },
  })

  const user = useMemo(() => {
    return (data?.users_by_pk ?? {}) as User
  }, [data])

  const userRolePermissions: Permission[] = useMemo(() => {
    // Important! In case the User is ADMIN or BU_ADMIN
    // add a special role based permission.
    // Remove this workaround when ADMIN and BU_ADMIN roles are removed
    if (isUserInRolesFn(user, ['ADMIN', 'BU_ADMIN'])) {
      return Object.values(ADMIN_PERMISSIONS).map(p => ({ title: p }))
    }

    if (!user.customer?.features?.includes('global_permissions')) {
      return Object.values(USER_PERMISSIONS).map(p => ({ title: p }))
    }

    return (data?.user_roles ?? [])
      .flatMap((r: UserRole) => r.role?.role_permissions)
      .filter((rP: RolePermissionRow) => rP)
      .map((rP: RolePermissionRow) => rP.permission)
  }, [data?.user_roles, user])

  const isUserInRoles = (roles: CustomerRoleType[]) =>
    isUserInRolesFn(user, roles)

  const hasUserPermissions = (permissions: PermissionType | PermissionType[]) =>
    hasUserPermissionsFn(userRolePermissions, permissions)

  const hasUserEnabledFeatures = (features: FeatureSwitch[]) =>
    hasUserEnabledFeaturesFn(user.customer?.features ?? [], features)

  return {
    error,
    loading,
    refetch,
    user,
    isUserInRoles,
    hasUserPermissions,
    hasUserEnabledFeatures,
  }
}
